import React from 'react';
import { Alert, Box, Chip, Paper, Stack, Typography } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import { CLASS_LOG_MODULES } from '../../constants/classLogModules';
import { formatCalendarDate } from '../../utils/dateTimeUtils';
import { buildNextPlanSummary, formatLmsActivities } from '../../utils/classSessionLog';

const MODULE_LABELS = Object.fromEntries(CLASS_LOG_MODULES.map((m) => [m.type, m.label]));

function formatTimeShort(timeStr) {
  if (!timeStr) return '';
  const part = String(timeStr).trim().slice(0, 8);
  const [h, m] = part.split(':').map(Number);
  if (Number.isNaN(h)) return '';
  const period = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  const min = m != null && !Number.isNaN(m) ? m : 0;
  return `${hour}:${String(min).padStart(2, '0')} ${period}`;
}

function PlanModuleRow({ mod, isV2 }) {
  const label = isV2
    ? mod.module_name || mod.module_id || 'Module'
    : MODULE_LABELS[mod.type] || mod.type;
  const level = String(mod.level || '').trim();
  const detail = isV2
    ? formatLmsActivities(mod)
    : String(mod.notes || '').trim();

  return (
    <Box
      sx={{
        py: 0.75,
        px: 1.25,
        borderRadius: 1.5,
        bgcolor: 'rgba(0,0,0,0.03)',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography variant="body2" fontWeight={800} color="text.primary">
        {label}
        {level ? (
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1, fontWeight: 700 }}>
            · Level {level}
          </Typography>
        ) : null}
      </Typography>
      {detail ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.45 }}>
          {isV2 ? `Activities: ${detail}` : detail}
        </Typography>
      ) : null}
    </Box>
  );
}

export default function PreviousClassPlanSection({ previousClassPlan }) {
  if (!previousClassPlan?.fromSessionId) return null;

  const { fromSessionDate, fromStartTime, nextPlanJson, hasPlan, summaryText } = previousClassPlan;
  const plan = nextPlanJson || {};
  const isV2 = Number(plan.version) === 2;
  const modules = plan.plannedModules || [];
  const legacyText = String(plan.legacyText || '').trim();
  const showPlan = hasPlan && (modules.length > 0 || legacyText);
  const resolvedSummary = summaryText || (showPlan ? buildNextPlanSummary(plan) : '');

  const dateLabel = fromSessionDate ? formatCalendarDate(fromSessionDate) : '';
  const timeLabel = formatTimeShort(fromStartTime);
  const subtitle = [dateLabel, timeLabel].filter(Boolean).join(' · ');

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'grey.300',
        bgcolor: '#fafafa',
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 2 },
          py: 1.25,
          bgcolor: 'grey.100',
          borderBottom: '1px solid',
          borderColor: 'grey.300',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap">
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <HistoryIcon fontSize="small" color="action" />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight={900} lineHeight={1.2}>
                Plan from last class
              </Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                What you planned to cover today
              </Typography>
            </Box>
          </Stack>
          {subtitle ? (
            <Chip label={subtitle} size="small" variant="outlined" sx={{ fontWeight: 700, maxWidth: '100%' }} />
          ) : null}
        </Stack>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 2 }, py: 1.5, maxHeight: 220, overflowY: 'auto' }}>
        {!showPlan ? (
          <Alert severity="info" sx={{ py: 0.5 }}>
            No plan was saved after the previous class.
          </Alert>
        ) : modules.length > 0 ? (
          <Stack spacing={1}>
            {modules.map((mod) => (
              <PlanModuleRow
                key={isV2 ? String(mod.module_id) : mod.type}
                mod={mod}
                isV2={isV2}
              />
            ))}
            {legacyText ? (
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5, pt: 0.5 }}>
                {legacyText.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()}
              </Typography>
            ) : null}
          </Stack>
        ) : resolvedSummary ? (
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
            {resolvedSummary.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
            {legacyText.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}
